import {
  generateApiKey,
  updateAllowedServerIp,
  updateWebhookUrl
} from '@basqet-dashboard/common/api';
import { ReactComponent as Copy } from '@basqet-dashboard/common/assets/icons/copy.svg';
import { Button, FormInput, Modal } from '@basqet-dashboard/common/components';
import { PERMISSIONS } from '@basqet-dashboard/common/config';
import { useHasPermission } from '@basqet-dashboard/common/hooks';
import {
  allowedServerIpSchema,
  formatErrorMessage,
  webhookSchema
} from '@basqet-dashboard/common/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFetchKeys } from '@hooks/queries';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

const APIPage = () => {
  const { keys, refetch } = useFetchKeys();
  const [isLoading, setIsLoading] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [showServerIpModal, setShowServerIpModal] = useState(false);
  const { register, setValue } = useForm({
    mode: 'onChange'
  });

  const hasPermission = useHasPermission();
  const canManageDeveloperTabs = hasPermission(PERMISSIONS.DEVELOPER_TAB_MANAGE);

  useEffect(() => {
    setValue('public_key', keys.public_key || '');
    setValue('secret_key', keys.secret_key || '');
    setValue('webhook_url', keys.webhook_url || '');
    setValue('allowed_server_ip', keys.allowed_server_ip || '');
  }, [keys]);

  useEffect(() => {
    if (!isLoading || !showWebhookModal || !showServerIpModal) {
      refetch();
    }
  }, [isLoading, showWebhookModal, showServerIpModal]);

  const copyToClipboard = (type) => {
    const el = document.createElement('textarea');
    el.value = type === 'public' ? keys.public_key : keys.secret_key;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    toast.success('Copied to clipboard');
  };

  const generateNewKeys = async () => {
    setIsLoading(true);
    try {
      await generateApiKey();
      setIsLoading(false);
    } catch (error) {
      formatErrorMessage(error);
      setIsLoading(false);
    }
  };

  return (
    <div className="settings">
      <div className="card">
        <h3 className="card__header">API keys</h3>
        <div className="key__group">
          <FormInput
            type="text"
            label="Public Key"
            name="public_key"
            autoComplete="new-password"
            {...register('public_key')}
            disabled
          />
          <button
            className="key__button"
            title="Copy public key"
            onClick={() => copyToClipboard('public')}>
            <Copy />
          </button>
        </div>
        <div className="key__group">
          <FormInput
            label="Secret Key"
            name="secret_key"
            type="password"
            autoComplete="new-password"
            {...register('secret_key')}
            disabled
          />
          <button
            className="key__button"
            title="Copy secret key"
            onClick={() => copyToClipboard('secret')}>
            <Copy />
          </button>
        </div>
        {canManageDeveloperTabs && (
          <Button className="settlements__button" onClick={generateNewKeys} isLoading={isLoading}>
            Regenerate API Keys
          </Button>
        )}
      </div>
      <div className="card">
        <h3 className="card__header">Webhook</h3>
        <FormInput
          label="Webhook URL"
          name="webhook_url"
          type="url"
          autoComplete="new-password"
          {...register('webhook_url')}
          disabled
        />
        {canManageDeveloperTabs && (
          <Button className="settlements__button" onClick={() => setShowWebhookModal(true)}>
            Update webhook URL
          </Button>
        )}
      </div>
      <div className="card">
        <h3 className="card__header">Server IP</h3>
        <FormInput
          label="Allowed Server IP"
          name="allowed_server_ip"
          type="text"
          autoComplete="off"
          {...register('allowed_server_ip')}
          disabled
        />
        {canManageDeveloperTabs && (
          <Button className="settlements__button" onClick={() => setShowServerIpModal(true)}>
            Update server IP
          </Button>
        )}
      </div>
      {showWebhookModal && (
        <NewWebHookModal url={keys.webhook_url} onModalClose={() => setShowWebhookModal(false)} />
      )}
      {showServerIpModal && (
        <AllowedServerIpModal
          allowedServerIp={keys.allowed_server_ip}
          onModalClose={() => setShowServerIpModal(false)}
        />
      )}
    </div>
  );
};

const NewWebHookModal = ({ onModalClose, url }) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(webhookSchema),
    defaultValues: {
      webhook_url: url
    }
  });

  const onSubmit = async (body) => {
    setIsLoading(true);
    try {
      await updateWebhookUrl(body);
      toast.success('Webhook URL updated successfully');
      setIsLoading(false);
      onModalClose();
    } catch (error) {
      formatErrorMessage(error);
      setIsLoading(false);
    }
  };
  return (
    <Modal onModalClose={onModalClose} header={{ name: 'Update Webhook', cursive: 'URL' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="settlements__form">
          <div className="settlements__form__group">
            <FormInput
              placeholder="Webhook URL"
              name="webhook_url"
              type="url"
              required
              error={errors.webhook_url && errors.webhook_url.message}
              {...register('webhook_url')}
            />
          </div>
        </div>
        <Button isFull type="submit" isLoading={isLoading}>
          Update Webhook URL
        </Button>
      </form>
    </Modal>
  );
};

NewWebHookModal.propTypes = {
  onModalClose: PropTypes.func,
  url: PropTypes.string
};

const AllowedServerIpModal = ({ onModalClose, allowedServerIp }) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(allowedServerIpSchema),
    defaultValues: {
      allowed_server_ip: allowedServerIp || ''
    }
  });

  const onSubmit = async ({ allowed_server_ip }) => {
    setIsLoading(true);
    try {
      const trimmed = (allowed_server_ip || '').trim();
      await updateAllowedServerIp({ allowed_server_ip: trimmed === '' ? null : trimmed });
      toast.success(
        trimmed === '' ? 'Server IP restriction cleared' : 'Server IP updated successfully'
      );
      setIsLoading(false);
      onModalClose();
    } catch (error) {
      formatErrorMessage(error);
      setIsLoading(false);
    }
  };

  return (
    <Modal onModalClose={onModalClose} header={{ name: 'Update Server', cursive: 'IP' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="settlements__form">
          <div className="settlements__form__group">
            <FormInput
              placeholder="e.g. 104.28.187.43"
              name="allowed_server_ip"
              type="text"
              error={errors.allowed_server_ip && errors.allowed_server_ip.message}
              {...register('allowed_server_ip')}
            />
          </div>
        </div>
        <Button isFull type="submit" isLoading={isLoading}>
          Update Server IP
        </Button>
      </form>
    </Modal>
  );
};

AllowedServerIpModal.propTypes = {
  onModalClose: PropTypes.func,
  allowedServerIp: PropTypes.string
};

export { APIPage };
